<template lang="pug">
BriqDialog(@close="close" dialogSize="briq-dialog-large")
  div(v-show="loading")
    p Loading...
  .integrations-modal-content(v-show="!loading")
    .briq-modal-header
      img(v-if="hasImage" :src="image")
      .image-placeholder(v-else)
      h2 {{ integration.Details.LocalizedName }}
    .briq-modal-body
      .description {{ integration.Details.LocalizedDescription }}
      .description Categories: {{ categories === '' ? 'none' : categories }}
      .description(v-if="integration.Details.InfoUrl") More information: 
        a(:href="integration.Details.InfoUrl" target="_blank") {{ integration.Details.InfoUrl }}
      .api-keys-info-holder(v-if="integration.ApiKeys")
        h3 Api Keys
        .api-keys-info(v-for="(apiKey, index) in integration.ApiKeys")
          .label {{ apiKey.Name }}
          .value {{ apiKey.Key }}
      .form
        .switch-wrapper
          Switches(class="active-switch" name="toggle-switch" v-model="integrationIsActive" theme="custom" color="blue" :disabled="integration.Details.AlwaysActive" label="Active")
          Switches(v-if="integration.LegacyMode !== 'Unavailable'" class="legacy-switch" name="toggle-switch" v-model="legacyModeIsActive" theme="custom" color="blue" :disabled="integration.IsLegacyModeReadOnly === true || integrationIsActive" label="Legacy mode")
        .settings(v-if="showSettings")
          AdvancedSettingsPanels(
            v-if="integration.Settings" 
            :settings="integration.Settings" 
            :hideSearchInput="true"
            :hideToggleAndKeepPanelsExpanded="true" 
            )
    .footer-description(v-if="integration.Details.LocalizedAdditionalInfo") {{ integration.Details.LocalizedAdditionalInfo }}
    .briq-modal-footer
      .button-bar.button-bar--left
        IntegrationActions(
          :actions="integration.Actions"
          :identificationCode="code"
          @close="close"
          @performRefetch="fetch"
          )
      .button-bar.button-bar--right.briq-buttons
        BriqButton.briq-button(variant="text" @click="close") Cancel
        BriqViewAuditTrail.briq-button(objectType="Integration" :objectKey="!loading && integration.Key ? integration.Key : ``")
        BriqSubmitButton.briq-button(:pending="pending" @click="save") Save
</template>

<script>
import { mapActions, mapMutations } from 'vuex'
import Switches from 'vue-switches'
import AdvancedSettingsPanels from '@v/settings/application/advanced/AdvancedSettingsPanels.vue'
import IntegrationActions from '@/vue/settings/application/integrations/IntegrationsModal/IntegrationActions.vue'

const Status = Object.freeze({
  Active: 'Active',
  Inactive: 'Inactive',
})

export default {
  components: {
    Switches,
    AdvancedSettingsPanels,
    IntegrationActions,
  },
  props: {
    code: String,
  },
  data() {
    return {
      integration: {
        Details: {},
      },
      loading: true,
      pending: false
    }
  },
  async mounted() {
    await this.fetch()
  },
  computed: {
    image() {
      return `data:${this.integration.Details.LocalizedImageMimeType};base64,${this.integration.Details.LocalizedImage}`
    },
    hasImage() {
      return this.integration.LocalizedImage !== null
    },
    integrationIsActive: {
      get() {
        return this.integration.Status === Status.Active
      },
      set(newValue) {
        this.integration.Status = newValue === true ? Status.Active : Status.Inactive
      },
    },
    legacyModeIsActive: {
      get() {
        return this.integration.LegacyMode === 'On'
      },
      set(newValue) {
        this.integration.LegacyMode = newValue === true ? 'On' : 'Off'
      },
    },
    showSettings() {
      return this.integration.Settings?.length > 0
    },
    categories() {
      if (this.integration.Details.Categories) {
        return this.integration.Details.Categories.join(', ')
      }

      return ''
    },
  },
  methods: {
    ...mapActions('integrations', [
      'fetchIntegration',
      'saveIntegration',
      'connectIntegration',
      'fetchIntegrations',
    ]),
    ...mapMutations('integrations', ['updateIntegration']),
    close() {
      let query = Object.assign({}, this.$route.query)
      if (query.open) {
        delete query.open
        this.$router.replace({ query })
      }
      this.$emit('close')
    },
    async fetch() {
      try {
        this.integration = await this.fetchIntegration(this.code)
      } catch (e) {
        this.$notifyError('Unable to fetch integration')
        this.close()
      }
      this.loading = false
    },
    getComponent({ DataType, MaxOccurs }) {
      if (DataType === 'String' || DataType === 'Integer' || DataType === 'Decimal')
        return MaxOccurs !== 1 ? 'StandardInputMultiple' : 'StandardInput'
      return DataType
    },
    async save() {
      this.pending = true
      try {
        this.integration.Settings = this.integration.Settings.filter(function(setting) {
          return setting.ReadOnly === false
        })

        await this.saveIntegration({ code: this.code, integration: this.integration })
        this.close()
        this.updateIntegration({
          code: this.code,
          status: this.integration.Status,
          legacyMode: this.integration.LegacyMode,
        })
        this.$notifySuccess('Integration saved')

        try {
          await this.fetchIntegrations()
        } catch (e) {
          this.$notifyError('Unable to fetch integrations')
          this.error = e
        }
      } catch (e) {
        this.pending = false
        this.$notifyError('Unable to save integration')
      }
    },
    async connect() {
      const model = {
        ReturnURL: `${location.protocol}//${location.host}${this.$router.currentRoute.fullPath}?open=${this.code}`,
      }
      try {
        const response = await this.connectIntegration({ code: this.code, model: model })
        if (response.data.URL) {
          location.replace(response.data.URL)
        }
      } catch (e) {
        this.$notifyError('Unable to connect integration')
      }
    },
    updateSetting(setting, newValue) {
      setting.Values = getNewSettingValues(setting)(newValue)
    },
  },
}

function getNewSettingValues(setting) {
  if (setting.DataType === 'MultiList') {
    return x => (x || []).map(v => v.Value)
  } else if (setting.MaxOccurs !== 1) {
    return x => x || []
  } else {
    return x => (x ? [x] : [])
  }
}
</script>

<style scoped lang="stylus">
img
  max-height 64px
  padding-bottom 10px

.image-placeholder
  height 64px

.integrations-modal-content
  min-height 72vh
  display flex
  flex-direction column

.briq-modal-header
  display block
  text-align center
  margin-right 0.4em
  line-height 1.4
  max-height 200px
  padding 10px 30px 14px 30px

.briq-modal-body
  text-align left
  max-height 68vh
  overflow-y auto

.briq-modal-footer
  margin-top auto
  min-height 30px
  text-align right
  padding 10px 0 0
  display flex
  justify-content space-between

.description
  margin-bottom 6px
  text-align center
  color #96999D

.footer-description
  margin-bottom 6px
  text-align left
  color #96999D

p
  text-align center

.fade-enter, .fade-leave-to
  opacity 0

.fade-enter-active, .fade-leave-active
  transition opacity .3s ease

.switch-wrapper
  text-align center

.connect
  text-align center
  margin-top 2em

.connect p
  margin-top 1em
  color rgba(52,58,64,0.35)
  font-style italic

.api-keys-info-holder
  margin 12px 0 30px 0
  text-align center

  .api-keys-info
    margin-top 10px
    display flex
    justify-content center

    .label
      padding-right 20px

.briq-buttons
  display flex
  > .briq-button
    margin 0px 5px 0px 5px

.active-switch
  width 75px

.legacy-switch
  width 130px
  margin-left 45px
</style>
<style>
.legacy-switch,
.active-switch {
  cursor: pointer;
  .vue-switcher__label {
    position: absolute;
    left: 42px;
    bottom: -10px;
    font-size: 0.9em;
  }
  input[type='checkbox'] {
    width: 1px;
  }
}
</style>
